import { CloseIcon, HamburgerIcon, Logo, UserIcon } from '@mote/assets/icons'
import { useMediaQuery } from '@mote/helpers'
import { ADMIN_ALLOW_LIST, EDUCATION_NAV_LINKS } from '@mote/helpers/constants'
import { getAuthAttribute } from '@mote/helpers/identity'
import { Block, Button, Dropdown } from '@mote/ui'
import React from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useAuth } from '../../auth/useAuth'
import { getOpenReplayTracker } from '../../openReplayTracker'
import { Content } from './content'

const navLinks = [
  {
    name: 'For business',
    url: '/business'
  },
  {
    name: 'Learning Hub',
    url: '/the-hub'
  },
  {
    name: 'Pricing',
    url: '/pricing'
  },
  {
    name: 'Get a quote',
    url: '/quote'
  }
]

export const Navbar = ({ className, hideNavContent = false }) => {
  const [showMobileNavItems, setShowMobileNavItems] = React.useState(false)
  const history = useHistory()
  const { user, isAuthenticated } = useAuth()
  const mediaBreakPoint = useMediaQuery([640, 992, 1200])
  const isMobile = React.useMemo(
    () => !hideNavContent && mediaBreakPoint <= 992,
    [mediaBreakPoint, hideNavContent]
  )
  const navbarTestId = React.useMemo(
    () => (isAuthenticated ? 'authenticated-navbar' : 'unauthenticated-navbar'),
    [isAuthenticated]
  )

  React.useEffect(() => {
    const bodyOverflow = document.body.style.overflow
    const bodyPaddingRight = document.body.style.paddingRight

    const root = document.getElementById('root')
    const rootOverflow = root ? root.style.overflow : ''
    const rootPaddingRight = root ? root.style.paddingRight : 0
    const scrollBarWidth = root ? root.offsetWidth - root.scrollWidth : 0

    if (showMobileNavItems) {
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollBarWidth}px`

      if (root) {
        root.style.overflow = 'hidden'
        root.style.paddingRight = `${scrollBarWidth}px`
      }
    } else {
      document.body.style.overflow = bodyOverflow
      document.body.style.paddingRight = bodyPaddingRight

      if (root) {
        root.style.overflow = rootOverflow
        root.style.paddingRight = rootPaddingRight
      }
    }

    return () => {
      document.body.style.overflow = bodyOverflow
      document.body.style.paddingRight = bodyPaddingRight

      if (root) {
        root.style.overflow = rootOverflow
        root.style.paddingRight = rootPaddingRight
      }
    }
  }, [showMobileNavItems])

  React.useEffect(() => {
    if (!user) return
    const tracker = getOpenReplayTracker()

    tracker?.setUserID(user.primaryEmailAddress)
  }, [user])

  React.useEffect(() => {
    if (!(!isMobile && showMobileNavItems)) return

    setShowMobileNavItems(false)
  }, [isMobile, showMobileNavItems])

  return (
    <Root data-testid={navbarTestId} className={className}>
      <Container>
        <LogoContainer onClick={_ => history.push('/')}>
          <Logo />
        </LogoContainer>
        {isMobile && (
          <MobileContainerToggle
            onClick={_ => setShowMobileNavItems(prev => !prev)}
          >
            {showMobileNavItems ? <CloseIcon /> : <HamburgerIcon />}
          </MobileContainerToggle>
        )}
        {!isMobile && <NavContent hideNavContent={hideNavContent} />}
      </Container>
      {isMobile && showMobileNavItems && <NavContent isMobile={true} />}
    </Root>
  )
}

const NavContent = ({ isMobile = false, hideNavContent }) => {
  const history = useHistory()
  const { pathname } = useLocation()
  const { user, isAuthenticated, logout } = useAuth()
  const emailAddress = React.useMemo(
    () => getAuthAttribute('primaryEmailAddress'),
    []
  )
  const {
    isHomePage,
    isLoginPage,
    isSignupPage,
    isOnboardingPage,
    isCheckoutPage
  } = React.useMemo(
    () => ({
      isHomePage: pathname === '/',
      isLoginPage: pathname === '/login',
      isSignupPage: pathname === '/signup',
      isOnboardingPage: pathname.startsWith('/onboarding'),
      isCheckoutPage: pathname.startsWith('/checkout')
    }),
    [pathname]
  )
  const { navLinkContainerProps, ctaContainerProps } = React.useMemo(() => {
    if (!isMobile)
      return {
        navLinkContainerProps: {
          layout: 'horizontal',
          justify: 'flex-start',
          gap: 32
        },
        ctaContainerProps: {
          layout: 'horizontal',
          gap: 16
        }
      }

    return {
      navLinkContainerProps: {
        layout: 'vertical',
        justify: 'center',
        spreadY: true,
        gap: 24
      },
      ctaContainerProps: {
        layout: 'vertical',
        gap: 24
      }
    }
  }, [isMobile])

  const { Wrapper, wrapperProps } = React.useMemo(() => {
    return isMobile
      ? { Wrapper: MobileContainer, wrapperProps: { isHomePage } }
      : { Wrapper: React.Fragment, wrapperProps: {} }
  }, [isMobile])

  async function handleLogout() {
    logout()
    history.push('/login')
  }

  function handleAccountDropdownSelection(key) {
    switch (key) {
      case 'my-account': {
        history.push('/account')
        break
      }
      case 'my-activity': {
        history.push('/account/activity')
        break
      }
      case 'logout': {
        handleLogout()
        break
      }
      case 'admin': {
        history.push('/admin')
        break
      }
      default:
      // no-op
    }
  }

  function handleForEducationDropdownSelection(key) {
    history.push(key)
  }

  // function handleForBusinessDropdownSelection(key) {
  //   history.push(key)
  // }

  return (
    <Wrapper {...wrapperProps}>
      {!hideNavContent && (
        <NavlinksContainer align='center' {...navLinkContainerProps}>
          <StyledDropdown
            width={240}
            inline={isMobile}
            onAction={handleForEducationDropdownSelection}
            triggerElementContent={
              <DropdownTriggerText>For education</DropdownTriggerText>
            }
            placement='bottom-left'
            transitionType='slide'
          >
            {EDUCATION_NAV_LINKS.map(educationLink => (
              <DropdownItem key={educationLink.url}>
                <Dropdown.ItemText>{educationLink.name}</Dropdown.ItemText>
              </DropdownItem>
            ))}
          </StyledDropdown>

          {/* <StyledDropdown
            width={240}
            inline={isMobile}
            onAction={handleForBusinessDropdownSelection}
            triggerElementContent={
              <DropdownTriggerText>For business</DropdownTriggerText>
            }
            placement='bottomLeft'
          >
            {BUSINESS_NAV_LINKS.map(businessLink => (
              <DropdownItem key={businessLink.url}>
                <Dropdown.ItemText>{businessLink.name}</Dropdown.ItemText>
              </DropdownItem>
            ))}
          </StyledDropdown> */}

          {navLinks.map(navLink => (
            <NavLink key={navLink.url} onClick={_ => history.push(navLink.url)}>
              {navLink.name}
            </NavLink>
          ))}
        </NavlinksContainer>
      )}
      <>
        {!isAuthenticated && (
          <Block align='center' justify='center' {...ctaContainerProps}>
            {!isLoginPage && (
              <SignInLink
                text='Sign in'
                testId='navbar-login-button'
                onClick={_ => history.push('/login')}
              />
            )}
            {!isSignupPage && (
              <SignUpLink
                text='Sign up free'
                testId='navbar-signup-button'
                onClick={_ => history.push('/signup')}
              />
            )}
          </Block>
        )}
        {isAuthenticated && (
          <>
            {(isOnboardingPage || isCheckoutPage) && (
              <StyledDropdown
                inline={isMobile}
                onAction={handleAccountDropdownSelection}
                data-trigger-testid='navbar-dropdown-trigger-button'
                triggerElementContent={
                  <DropdownTriggerText>{emailAddress}</DropdownTriggerText>
                }
                placement='bottom-right'
              >
                {ADMIN_ALLOW_LIST.includes(user.primaryEmailAddress) && (
                  <DropdownItem
                    key='admin'
                    data-testid='navbar-dropdown-admin-option'
                  >
                    <Dropdown.ItemText>Admin</Dropdown.ItemText>
                  </DropdownItem>
                )}
                <DropdownItem
                  key='logout'
                  data-testid='navbar-dropdown-logout-option'
                >
                  <Dropdown.ItemText>Sign out</Dropdown.ItemText>
                </DropdownItem>
              </StyledDropdown>
            )}
            {!(isOnboardingPage || isCheckoutPage) && (
              <StyledDropdown
                inline={isMobile}
                onAction={handleAccountDropdownSelection}
                data-trigger-testid='navbar-dropdown-trigger-button'
                triggerElementContent={
                  <DropdownTriggerText>
                    <UserIcon height='1.5em' width='1.5em' />
                    Account
                  </DropdownTriggerText>
                }
                placement='bottom-right'
              >
                <DropdownHeader key='email-address'>
                  <span>{emailAddress}</span>
                </DropdownHeader>
                {ADMIN_ALLOW_LIST.includes(user.primaryEmailAddress) && (
                  <DropdownItem
                    key='admin'
                    data-testid='navbar-dropdown-admin-option'
                  >
                    <Dropdown.ItemText>Admin</Dropdown.ItemText>
                  </DropdownItem>
                )}

                <DropdownItem
                  key='my-account'
                  data-testid='navbar-dropdown-my-account-option'
                >
                  <Dropdown.ItemText>My Account</Dropdown.ItemText>
                </DropdownItem>

                <DropdownItem
                  key='my-activity'
                  data-testid='navbar-dropdown-my-activity-option'
                >
                  <Dropdown.ItemText>My Activity</Dropdown.ItemText>
                </DropdownItem>

                <DropdownItem
                  key='logout'
                  data-testid='navbar-dropdown-logout-option'
                >
                  <Dropdown.ItemText>Sign out</Dropdown.ItemText>
                </DropdownItem>
              </StyledDropdown>
            )}
          </>
        )}
      </>
    </Wrapper>
  )
}

const Root = styled.header`
  z-index: 20;
  background-color: #ffffff;
`

const Container = styled(Content)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 90px;
`

const LogoContainer = styled.div`
  cursor: pointer;
  margin-right: 32px;

  @media (max-width: 991px) {
    margin-right: 16px;
  }
`

const MobileContainer = styled.div`
  z-index: 20;
  position: fixed;
  top: ${({ isHomePage }) => (isHomePage ? 140 : 90)}px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px 90px;
  background: #ffffff;
  overflow-y: auto;
`

const MobileContainerToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #f6f6f9;
  }
`

const NavlinksContainer = styled(Block)`
  flex-grow: 1;
  padding: 0 16px;

  @media (max-width: 991px) {
    flex-grow: unset;
    padding: 0;
    height: auto;
    margin-bottom: 24px;
  }
`

const NavLink = styled.a`
  display: block;
  color: #25252d;
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 120%;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    color: #ac0ae8;
  }

  &:active {
    color: #9b09d1;
  }

  @media (max-width: 991px) {
    font-size: 16px;
    text-align: center;
  }
`

const NavLinkBtn = styled(Button)`
  font-size: 15px;
  line-height: 120%;
  color: #ffffff;

  span {
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }

  @media (max-width: 991px) {
    font-size: 16px;
  }
`

const SignInLink = styled(NavLinkBtn)`
  color: #25252d;
  background: #f6f6f9 !important;

  &:hover {
    color: #ac0ae8;
  }

  &:active {
    color: #9b09d1;
  }
`

const SignUpLink = styled(NavLinkBtn)``

const StyledDropdown = styled(Dropdown)`
  .dropdown-trigger-container {
    font-weight: 500;
    border: none;

    &:hover {
      background: transparent;
    }
  }

  @media (max-width: 374px) {
    max-width: 170px;
  }

  ${({ inline }) =>
    inline &&
    css`
      @media (max-width: 991px) {
        display: inline-flex;
        flex-direction: column;

        .dropdown-trigger-container {
          font-size: 16px;

          span {
            text-align: center;
          }

          &:focus {
            box-shadow: none;
          }
        }

        .dropdown-container {
          li {
            justify-content: center;
          }

          .dropdown-item {
            &:focus {
              color: #ac0ae8;
              background: transparent;
            }
          }
        }
      }

      @media (max-width: 374px) {
        max-width: unset;
      }
    `};
`

const DropdownItem = styled(Dropdown.Item)``

const DropdownHeader = styled(Dropdown.Header)`
  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const DropdownTriggerText = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;

  & a {
    color: inherit;
    pointer-events: none;
  }
`
